import * as React from 'react';
import {
  BottomSheet,
  BottomSheetContent,
  Button,
  CallCard,
  Card,
  CardBody,
  ChatBubble,
  ChatComposer,
  ChatReveal,
  DetailField,
  Icon,
  Inline,
  ListRow,
  MemberRow,
  MobileHeader,
  MobileScreen,
  PriorityTag,
  Stack,
  StatusChip,
  Text,
  toast,
  ChatWorking,
} from '@relay-towing/ui';
import {
  ArrowSquareOut,
  Camera,
  CaretDown,
  CaretUp,
  Car,
  Check,
  ListBullets,
  NotePencil,
  Plus,
} from '@phosphor-icons/react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useGetAllDrivers } from '../../api/hooks/useGetAllDrivers';
import { useGetCustomers } from '../../api/hooks/useGetCustomers';
import { useCreateCall } from '../../hooks/useCreateCall';
import { useDispatchParse } from '../../hooks/useDispatchParse';
import { useMember } from '../../hooks/useMember';
import {
  createCallSchema,
  generateDispatchNumber,
  toCreateCallRequest,
} from '../../lib/createCallSchema';
import { useBack } from '../shell/useBack';
import { dutyLabel, dutyTone, truckLabel } from '../status';
import { CallDraftCard, type CardActions } from './CallDraftCard';
import {
  clock,
  firstName,
  type Chat,
  type ChatDriver,
  type EditKey,
  type Message,
} from './chat';
import {
  WEIGHT_LABEL,
  blankCall,
  matchAccount,
  parseCall,
  parseVehicle,
  pickupLine,
  splitPickup,
  toCreateValues,
  vehicleText,
} from './parseCall';
import {
  changedOnSave,
  filledByAi,
  fromParsedFields,
  withChanged,
} from './readFields';

/** What the chat says while the AI reads a call. Timed, in the order it reads. */
const READING_STEPS = [
  'Reading the text',
  'Finding the pickup',
  'Reading the vehicle',
  'Matching the account',
  'Filling in the card',
] as const;

/** What the chat says while it creates the call, which takes a while. */
const CREATING_STEPS = [
  'Creating the call',
  'Saving it to your call log',
  'Getting the call number',
] as const;

/** How long a read waits for the account and driver lists past the parse. */
const LISTS_WAIT_MS = 3000;

const YES = /^(y|yes|yep|yeah|correct|right|looks good|dispatch|send|go)\b/i;
const NO = /^(n|no|nope|wrong|change|not)\b/i;

let nextId = 0;
const uid = () => `m${++nextId}`;

function newChat(): Chat {
  return {
    id: uid(),
    timeLabel: clock(),
    call: blankCall(),
    notes: [],
    phase: 'idle',
    messages: [],
    accountId: null,
    driverId: undefined,
    driversOpen: true,
    driversAll: false,
    editing: null,
    editingNotes: false,
    shown: {},
    created: null,
    reading: null,
    changed: [],
  };
}

const say = (text: string, muted = false): Message => ({
  id: uid(),
  kind: 'assistant',
  text,
  muted,
});

/** Drivers who can take a call now first, then those on one, then the rest. */
const DUTY_ORDER: Record<string, number> = {
  ACTIVE: 0,
  CLEAR: 0,
  DISPATCHED: 1,
};

/** Drivers who can be sent now. The open list shows the rest on request. */
const ON_DUTY = new Set(['ACTIVE', 'CLEAR', 'DISPATCHED']);

/** The steps a card's reveal takes: itself, its four sections, its note. */
const CARD_STEPS = 6;

/** How many drivers on duty the folded list shows. */
const SHORT_LIST = 5;

/**
 * The dispatch chat, rebuilt from Mike's "Relay Dispatch Chat" prototype:
 * paste a dispatch text, and the chat reads it into a call, asks for the
 * pickup if it is missing, asks who should take it from the company's real
 * drivers, and checks before it goes out. Dispatch creates the call
 * through the same request the create sheet makes, assigned to the driver
 * picked, and links to it. Each call is a chat of its own; the Calls
 * button lists the ones made here. Opened from Functions, and only by an
 * owner who can create calls, since every paste is a paid read.
 */
export function DispatchChatScreen() {
  const member = useMember();
  if (member.isLoading) return null;
  // Owners only for now, as its Functions item (driverAppTabs.ts).
  if (member.officeRole !== 'OWNER' || !member.can('calls.create'))
    return <Navigate to="/" replace />;
  return <DispatchChat />;
}

function DispatchChat() {
  const navigate = useNavigate();
  const back = useBack('/');
  const member = useMember();
  const {
    data: driverRows,
    isLoading: driversLoading,
    isError: driversFailed,
  } = useGetAllDrivers();
  const { data: customers, isLoading: customersLoading } = useGetCustomers();
  const { mutate: createCall } = useCreateCall();
  const dispatchParse = useDispatchParse();
  /** Chats whose feedback went out, so each sends it once. */
  const feedbackSent = React.useRef(new Set<string>());

  const [chats, setChats] = React.useState<Chat[]>(() => [newChat()]);
  const [currentId, setCurrentId] = React.useState(() => chats[0].id);
  const [view, setView] = React.useState<'chat' | 'list'>('chat');
  const [draft, setDraft] = React.useState('');
  const [adding, setAdding] = React.useState(false);
  const inputRef = React.useRef<HTMLTextAreaElement | null>(null);

  const chat = chats.find((one) => one.id === currentId) ?? chats[0];
  const cardId = `dispatch-card-${chat.id}`;

  const update = React.useCallback(
    (id: string, change: (was: Chat) => Partial<Chat>) =>
      setChats((all) =>
        all.map((one) => (one.id === id ? { ...one, ...change(one) } : one))
      ),
    []
  );
  const post = React.useCallback(
    (id: string, ...messages: Message[]) =>
      update(id, (was) => ({ messages: [...was.messages, ...messages] })),
    [update]
  );

  const drivers: ChatDriver[] = React.useMemo(
    () =>
      (driverRows ?? [])
        .filter((row) => row.user?.id)
        .map((row) => ({
          userId: row.user!.id,
          name: row.name,
          truckNumber: row.truckNumber,
          status: row.status,
        }))
        .sort(
          (a, b) =>
            (DUTY_ORDER[a.status ?? ''] ?? 2) -
              (DUTY_ORDER[b.status ?? ''] ?? 2) || a.name.localeCompare(b.name)
        ),
    [driverRows]
  );
  const driverOf = (c: Chat) =>
    c.driverId ? (drivers.find((d) => d.userId === c.driverId) ?? null) : null;

  const accounts = React.useMemo(
    () =>
      (customers?.data ?? []).map((c) => ({
        id: c.id,
        accountName: c.accountName,
      })),
    [customers]
  );

  /*
   * The parse names the account and driver by id, and the chat can only
   * keep them once it has the lists to find them in. A paste right after
   * the screen opens can beat the lists, so the read waits for them to
   * settle (loaded or failed), for at most LISTS_WAIT_MS, and then uses
   * the lists as they are.
   */
  const lists = React.useRef({ accounts, driverRows, settled: false });
  const waitingForLists = React.useRef<Array<() => void>>([]);
  const listsSettled = !customersLoading && !driversLoading;
  React.useEffect(() => {
    lists.current = { accounts, driverRows, settled: listsSettled };
    if (listsSettled) waitingForLists.current.splice(0).forEach((go) => go());
  }, [accounts, driverRows, listsSettled]);
  const whenListsSettle = () =>
    lists.current.settled
      ? Promise.resolve()
      : new Promise<void>((go) => {
          waitingForLists.current.push(go);
          window.setTimeout(go, LISTS_WAIT_MS);
        });

  const accountOptions = accounts.map((a) => ({
    value: a.id,
    label: a.accountName,
  }));
  /**
   * The account a chat's call goes on: picked, or named in the text. It
   * stays empty otherwise, and the dispatcher picks one before it goes out.
   * No company default: a private caller is not the default account.
   */
  const accountFor = (c: Chat) => {
    if (c.accountId) return accounts.find((a) => a.id === c.accountId) ?? null;
    return matchAccount(c.call.account, accounts);
  };

  /*
   * A reply lays itself out in order: each new message a beat after the one
   * before, and a card's sections a beat apart inside it. A message's step
   * is fixed the first time it shows, so a later render never moves it.
   */
  const steps = React.useRef(new Map<string, number>());
  let nextStep = 0;
  for (const message of chat.messages) {
    if (steps.current.has(message.id)) continue;
    steps.current.set(message.id, nextStep);
    nextStep += message.kind === 'card' ? CARD_STEPS : 1;
  }
  const stepOf = (message: Message) => steps.current.get(message.id) ?? 0;

  /*
   * New messages come into view from their first line: the chat scrolls so
   * the start of the reply sits under the header and the rest fills in below
   * it, never past it. A short reply only scrolls as far as the page goes.
   */
  const count = chat.messages.length;
  const seen = React.useRef(count);
  React.useEffect(() => {
    const from = seen.current;
    seen.current = count;
    if (view !== 'chat' || count <= from) return;
    const first = chat.messages[from];
    const el = first
      ? document.querySelector(`[data-message="${first.id}"]`)
      : null;
    const still = window.matchMedia?.(
      '(prefers-reduced-motion: reduce)'
    )?.matches;
    el?.scrollIntoView?.({
      block: 'start',
      behavior: still ? 'auto' : 'smooth',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [count, view]);

  const scrollToCard = () =>
    document.getElementById(cardId)?.scrollIntoView?.({ block: 'start' });

  /* ---------- the flow ---------- */

  const askDriver = (id: string, c: Chat) => {
    const { street, city } = splitPickup(c.call.pickup);
    const who = c.call.account || 'New call';
    const why = c.call.reason ? ` · ${c.call.reason.toLowerCase()}` : '';
    const where = city ? `${street}, ${city}` : street;
    const question =
      c.call.towType === 'LIGHT'
        ? "Who should take it? It's light duty, so any truck works."
        : c.call.towType === 'HEAVY'
          ? "Who should take it? It's heavy duty."
          : 'Who should take it?';
    update(id, (was) => ({
      phase: 'driver',
      driversOpen: true,
      messages: [
        ...was.messages,
        say(
          `${who}${why} at ${where}. Here's the call. Tap anything to change it.`
        ),
        { id: uid(), kind: 'card' },
        say(question),
        { id: uid(), kind: 'drivers' },
      ],
    }));
  };

  /** The text read, then on: the pickup if it is missing, else the driver. */
  const afterReading = (id: string, c: Chat, gotIt: boolean) => {
    if (!c.call.pickup) {
      update(id, () => ({ phase: 'pickup' }));
      post(
        id,
        say(
          gotIt
            ? "Got it. Where's the pickup? An address or an intersection works."
            : "On it. Where's the pickup? An address or an intersection works."
        )
      );
      return;
    }
    askDriver(id, c);
  };

  /** Today's rules parser, as the chat read texts before the backend could. */
  const readByRules = (id: string, c: Chat, text: string) => {
    const parsed = parseCall(text);
    const next: Chat = {
      ...c,
      call: parsed.call,
      notes: parsed.notes,
      reading: { by: 'rules', filled: [], suggestedDriverId: null },
    };
    update(id, () => ({
      phase: 'idle',
      call: next.call,
      notes: next.notes,
      reading: next.reading,
    }));
    afterReading(id, next, parsed.structured);
  };

  /**
   * Reads the text with the backend's parser, and with the rules parser
   * when that fails, answers an error or takes over 10 seconds.
   */
  const intake = (id: string, c: Chat, text: string) => {
    update(id, () => ({ phase: 'reading' }));
    dispatchParse
      .parse(text)
      .then((answer) => whenListsSettle().then(() => answer))
      .then(
        ({ fields, timeZone }) => {
          const { accounts: loaded, driverRows: rows } = lists.current;
          const read = fromParsedFields(fields, {
            timeZone,
            accountIds: loaded.map((a) => a.id),
            driverUserIdOf: (driverId) =>
              (rows ?? []).find((row) => row.id === driverId)?.user?.id ?? null,
          });
          const next: Chat = {
            ...c,
            call: read.call,
            notes: read.notes,
            accountId: read.accountId,
            driverId: read.driverUserId ?? undefined,
            reading: {
              by: 'ai',
              filled: filledByAi(fields),
              suggestedDriverId: read.driverUserId,
            },
          };
          update(id, () => ({
            phase: 'idle',
            call: next.call,
            notes: next.notes,
            accountId: next.accountId,
            driverId: next.driverId,
            reading: next.reading,
          }));
          afterReading(id, next, true);
        },
        () => readByRules(id, c, text)
      );
  };

  const showConfirm = (id: string, driver: ChatDriver | null) => {
    update(id, (was) => ({
      phase: 'confirm',
      driversOpen: false,
      messages: [
        ...was.messages,
        say(
          driver
            ? `Got it. ${firstName(driver.name)} is up. Here's a quick look before it goes out.`
            : "Okay, we'll leave it unassigned for now. Here's a quick look before we save it."
        ),
        { id: uid(), kind: 'confirm' },
      ],
    }));
  };

  const pickDriver = (c: Chat, driver: ChatDriver | null, echo: boolean) => {
    const userId = driver ? driver.userId : null;
    update(c.id, (was) => ({
      driverId: userId,
      // A change only when the reading named a driver and this is another.
      changed:
        was.reading?.suggestedDriverId &&
        was.reading.suggestedDriverId !== userId
          ? withChanged(was.changed, ['driverId'])
          : was.changed,
    }));
    if (echo)
      post(c.id, {
        id: uid(),
        kind: 'user',
        text: driver ? `Assign ${firstName(driver.name)}` : 'Leave unassigned',
      });
    if (c.phase === 'driver') showConfirm(c.id, driver);
    else {
      update(c.id, () => ({ driversOpen: false }));
      toast.info(
        driver ? `${firstName(driver.name)} assigned` : 'Left unassigned'
      );
    }
  };

  const pickByText = (c: Chat, text: string) => {
    const low = text.toLowerCase();
    if (/^(skip|later|none|unassigned|no one)/i.test(text)) {
      pickDriver(c, null, false);
      return true;
    }
    const hit = drivers.find(
      (d) =>
        low.includes(firstName(d.name).toLowerCase()) ||
        d.name.toLowerCase().includes(low)
    );
    if (!hit) return false;
    pickDriver(c, hit, false);
    return true;
  };

  const openEdit = (c: Chat, key: EditKey) => {
    update(c.id, (was) => ({
      editing: key,
      shown: { ...was.shown, [key]: true },
    }));
    window.setTimeout(scrollToCard, 30);
  };

  /** A change asked for in words: "change the drop-off". */
  const changeByText = (c: Chat, text: string) => {
    const low = text.toLowerCase();
    if (/pick ?up|location/.test(low)) return openEdit(c, 'pickup'), true;
    if (/drop|dest|yard/.test(low)) return openEdit(c, 'destination'), true;
    if (/vehicle|car|model|make/.test(low)) return openEdit(c, 'vehicle'), true;
    if (/priorit|urgent|emergency/.test(low))
      return openEdit(c, 'priority'), true;
    if (/note/.test(low)) {
      update(c.id, () => ({ editingNotes: true }));
      window.setTimeout(scrollToCard, 30);
      return true;
    }
    if (/driver|assign/.test(low)) {
      update(c.id, () => ({ driversOpen: true }));
      return true;
    }
    return pickByText(c, text);
  };

  const create = (c: Chat) => {
    if (c.phase !== 'confirm') return;
    const driver = driverOf(c);
    if (!member.can('calls.create')) {
      post(
        c.id,
        say(
          "This account can't create calls. An owner can give you Create calls."
        )
      );
      return;
    }
    const account = accountFor(c);
    if (!account) {
      post(c.id, say('Pick the account on the call first.'));
      window.setTimeout(scrollToCard, 30);
      return;
    }
    const checked = createCallSchema.safeParse(
      toCreateValues(c.call, c.notes, {
        accountId: account.id,
        accountName: account.accountName,
        drivers: driver ? [driver.userId] : [],
        dispatchNumber: generateDispatchNumber(),
      })
    );
    // The schema is the one place that says what a call needs, as it is
    // for the create sheet.
    if (!checked.success) {
      post(
        c.id,
        say(checked.error.issues[0]?.message ?? 'The call is missing a field.')
      );
      window.setTimeout(scrollToCard, 30);
      return;
    }
    update(c.id, () => ({
      phase: 'creating',
      editing: null,
      editingNotes: false,
    }));
    createCall(toCreateCallRequest(checked.data, {}), {
      onSuccess: (response: unknown) => {
        sendFeedback(c);
        // POST /calls/new-call answers with the call's id and number.
        const made = (response ?? {}) as {
          id?: unknown;
          dispatchNumber?: unknown;
        };
        const id = typeof made.id === 'string' ? made.id : null;
        const number =
          typeof made.dispatchNumber === 'string' ? made.dispatchNumber : '';
        const head = number ? `Call #${number}` : 'The call';
        update(c.id, (was) => ({
          phase: 'done',
          created: { id, dispatchNumber: number },
          messages: [
            ...was.messages,
            say(
              driver
                ? `${head} is created and assigned to ${driver.name}. It went out to their phone.`
                : `${head} is created and waiting for a driver. Assign one from the call.`
            ),
            { id: uid(), kind: 'created' },
          ],
        }));
      },
      onError: (error) => {
        update(c.id, (was) => ({
          phase: 'confirm',
          messages: [
            ...was.messages,
            say(
              `That didn't go through, so nothing was created. ${
                error instanceof Error ? error.message : ''
              }`.trim()
            ),
          ],
        }));
      },
    });
  };

  /** How the reading went, once per chat, after the call is created. */
  const sendFeedback = (c: Chat) => {
    if (!c.reading || feedbackSent.current.has(c.id)) return;
    feedbackSent.current.add(c.id);
    dispatchParse.feedback({
      fieldsChanged: c.changed,
      fieldsFilledByAi: c.reading.by === 'ai' ? c.reading.filled : [],
      fallbackUsed: c.reading.by === 'rules',
      dispatched: true,
    });
  };

  const answerDone = (c: Chat) => {
    const driver = driverOf(c);
    post(
      c.id,
      say(
        driver
          ? `It's with ${firstName(driver.name)}. Open the call to follow it, or paste the next one.`
          : "It's created and waiting for a driver. Paste the next call whenever."
      )
    );
  };

  const startChat = (text?: string) => {
    const fresh = newChat();
    setChats((all) => [...all, fresh]);
    setCurrentId(fresh.id);
    setView('chat');
    if (text) {
      // The new chat is not in state until the next render.
      window.setTimeout(() => submitTo(fresh, text), 0);
    } else inputRef.current?.focus();
  };

  const submitTo = (c: Chat, raw: string) => {
    const text = raw.trim();
    if (!text || c.phase === 'creating' || c.phase === 'reading') return;
    if (c.phase === 'done' && (/\n/.test(text) || text.length > 40)) {
      startChat(text);
      return;
    }
    post(c.id, { id: uid(), kind: 'user', text });
    switch (c.phase) {
      case 'idle':
        intake(c.id, c, text);
        return;
      case 'pickup': {
        const call = { ...c.call, pickup: text };
        update(c.id, (was) => ({
          call,
          changed: withChanged(
            was.changed,
            changedOnSave('pickup', was.call, text)
          ),
        }));
        askDriver(c.id, { ...c, call });
        return;
      }
      case 'driver':
        if (pickByText(c, text) || changeByText(c, text)) return;
        post(c.id, say('Tap a driver above, or type their first name.'));
        return;
      case 'confirm':
        if (YES.test(text)) return create(c);
        if (NO.test(text)) {
          toast.info('Tap anything above to change it');
          return scrollToCard();
        }
        if (changeByText(c, text)) return;
        post(
          c.id,
          say(
            'Tap anything in the summary to change it, or say yes to send it.'
          )
        );
        return;
      case 'done':
        answerDone(c);
        return;
    }
  };

  const send = (text: string) => {
    setDraft('');
    submitTo(chat, text);
  };

  /** The current chat with `change` made, and the fields it changed noted. */
  const editCall = (
    change: (was: Chat) => Partial<Chat>,
    fields: (was: Chat) => ReturnType<typeof changedOnSave>
  ) =>
    update(chat.id, (was) => ({
      ...change(was),
      changed: withChanged(was.changed, fields(was)),
    }));

  const actions: CardActions = {
    edit: (key) => update(chat.id, () => ({ editing: key })),
    save: (key, value) =>
      editCall(
        (was) => ({
          editing: null,
          call:
            key === 'vehicle'
              ? { ...was.call, ...parseVehicle(value) }
              : { ...was.call, [key]: value.trim() },
        }),
        (was) => changedOnSave(key, was.call, value)
      ),
    setPriority: (priority) =>
      editCall(
        (was) => ({ editing: null, call: { ...was.call, priority } }),
        (was) => (was.call.priority === priority ? [] : ['priority'])
      ),
    setWeight: (towType) =>
      editCall(
        (was) => ({ editing: null, call: { ...was.call, towType } }),
        (was) => (was.call.towType === towType ? [] : ['weightClass'])
      ),
    show: (key) => openEdit(chat, key),
    editNotes: (open) => update(chat.id, () => ({ editingNotes: open })),
    saveNotes: (notes) =>
      editCall(
        () => ({ notes, editingNotes: false }),
        (was) => (was.notes.join('\n') === notes.join('\n') ? [] : ['notes'])
      ),
    setAccount: (accountId) =>
      editCall(
        () => ({ accountId }),
        (was) => (accountFor(was)?.id === accountId ? [] : ['accountId'])
      ),
  };

  const copySummary = (c: Chat) => {
    const driver = driverOf(c);
    const text = [
      `Call #${c.created?.dispatchNumber || '(new)'} · ${accountFor(c)?.accountName ?? c.call.account}`,
      `Pickup: ${pickupLine(c.call.pickup)}`,
      `Drop-off: ${c.call.destination || 'not set'}`,
      `Vehicle: ${vehicleText(c.call) || 'not set'}${
        c.call.towType ? ` (${WEIGHT_LABEL[c.call.towType]})` : ''
      }`,
      `Driver: ${driver ? driver.name : 'unassigned'}`,
      `Notes: ${c.notes.join('; ') || 'none'}`,
    ].join('\n');
    navigator.clipboard?.writeText(text).then(
      () => toast.success('Summary copied'),
      () => toast.error("Couldn't copy here")
    );
  };

  /* ---------- the pieces of a chat ---------- */

  const driverPicker = (c: Chat) => {
    const chosen = driverOf(c);
    const editable = !c.created && c.phase !== 'creating';
    const open = c.driversOpen && editable;
    // Folded, the list shows a few drivers on duty; opened all the way, it
    // shows everyone, invited and off duty too. A driver already picked
    // stays in view either way.
    const onDuty = drivers.filter((d) => ON_DUTY.has(d.status ?? ''));
    const short = onDuty.slice(0, SHORT_LIST);
    if (chosen && !short.some((d) => d.userId === chosen.userId))
      short.unshift(chosen);
    const listed = c.driversAll ? drivers : short;
    const more = drivers.length - listed.length;
    const Caret = open ? CaretUp : CaretDown;
    const header = (
      <ListRow
        inset="tap"
        as={editable ? 'button' : 'div'}
        interactive={editable}
        aria-expanded={editable ? open : undefined}
        onClick={
          editable
            ? () =>
                update(c.id, (was) => ({
                  driversOpen: !was.driversOpen,
                  driversAll: false,
                }))
            : undefined
        }
      >
        <Stack gap={0} grow>
          <Text as="span" variant="body-lg-strong" truncate>
            {chosen ? chosen.name : 'Assign driver'}
          </Text>
          <Text as="span" variant="body-sm" tone="muted" truncate>
            {chosen
              ? truckLabel(chosen.truckNumber)
              : driversLoading
                ? 'Loading drivers'
                : `${onDuty.length} on duty`}
          </Text>
        </Stack>
        {editable ? <Icon icon={Caret} size="md" tone="muted" /> : null}
      </ListRow>
    );
    return (
      <Card bordered radius="xl" elevation="flat" padding="sm">
        <CardBody>
          <Stack gap={2}>
            {header}
            {!open ? null : driversLoading ? null : driversFailed ? (
              <Text tone="muted">
                The drivers didn't load. You can leave it unassigned.
              </Text>
            ) : drivers.length === 0 ? (
              <Text tone="muted">No drivers on this company yet.</Text>
            ) : (
              <Stack divided>
                {listed.map((driver) => (
                  <MemberRow
                    as="button"
                    interactive
                    inset="tap"
                    key={driver.userId}
                    name={driver.name}
                    detail={truckLabel(driver.truckNumber)}
                    aria-pressed={driver.userId === c.driverId}
                    control={
                      <StatusChip
                        label={dutyLabel(driver.status)}
                        tone={dutyTone(driver.status)}
                      />
                    }
                    onClick={() => pickDriver(c, driver, true)}
                  />
                ))}
              </Stack>
            )}
            {open && !driversLoading && !driversFailed ? (
              <>
                {onDuty.length === 0 && !c.driversAll && drivers.length > 0 ? (
                  <Text tone="muted">No one is on duty right now.</Text>
                ) : null}
                <Inline gap={2} wrap>
                  {more > 0 || c.driversAll ? (
                    <Button
                      variant="secondary"
                      pill
                      size="md"
                      onClick={() =>
                        update(c.id, (was) => ({ driversAll: !was.driversAll }))
                      }
                    >
                      {c.driversAll
                        ? 'Show fewer'
                        : `Show all ${drivers.length} drivers`}
                    </Button>
                  ) : null}
                  <Button
                    variant="subtle"
                    pill
                    size="md"
                    onClick={() => pickDriver(c, null, true)}
                  >
                    Leave unassigned for now
                  </Button>
                </Inline>
              </>
            ) : null}
          </Stack>
        </CardBody>
      </Card>
    );
  };

  const confirmCard = (c: Chat) => {
    const driver = driverOf(c);
    const account = accountFor(c);
    const editable = !c.created && c.phase !== 'creating';
    const vehicle = [
      vehicleText(c.call),
      c.call.towType ? WEIGHT_LABEL[c.call.towType] : '',
    ]
      .filter(Boolean)
      .join(' · ');
    const pencil = (label: string, onClick: () => void) =>
      editable ? (
        <Button
          variant="secondary"
          size="icon-lg"
          pill
          aria-label={`Change ${label.toLowerCase()}`}
          onClick={onClick}
        >
          <NotePencil />
        </Button>
      ) : undefined;
    return (
      <Card bordered radius="xl" elevation="flat" padding="md">
        <CardBody>
          <Stack gap={4}>
            <Inline justify="between" align="center">
              <Text as="h2" variant="h2">
                {c.created ? 'Created' : 'Is this correct?'}
              </Text>
              <PriorityTag priority={c.call.priority} />
            </Inline>
            <Stack divided gap={3}>
              <DetailField
                label="Account"
                value={account?.accountName ?? 'Pick an account'}
                action={pencil('Account', scrollToCard)}
              />
              <DetailField
                label="Pickup"
                value={pickupLine(c.call.pickup)}
                action={pencil('Pickup', () => openEdit(c, 'pickup'))}
              />
              <DetailField
                label="Drop-off"
                value={c.call.destination || 'Not set'}
                action={pencil('Drop-off', () => openEdit(c, 'destination'))}
              />
              <DetailField
                label="Vehicle"
                value={vehicle || 'Not set'}
                action={pencil('Vehicle', () => openEdit(c, 'vehicle'))}
              />
              <DetailField
                label="Driver"
                value={
                  driver
                    ? `${driver.name} · ${truckLabel(driver.truckNumber)}`
                    : 'Unassigned'
                }
                action={pencil('Driver', () =>
                  update(c.id, () => ({ driversOpen: true }))
                )}
              />
              <DetailField
                label="Notes"
                value={
                  c.notes.length
                    ? `${c.notes.length} saved · ${c.notes.slice(0, 2).join(' · ')}`
                    : 'None'
                }
                action={pencil('Notes', () => {
                  update(c.id, () => ({ editingNotes: true }));
                  window.setTimeout(scrollToCard, 30);
                })}
              />
            </Stack>
            {c.created ? null : (
              <Stack gap={2}>
                <Button
                  variant="soft"
                  size="xl"
                  pill
                  fullWidth
                  leadingIcon={<Check />}
                  loading={c.phase === 'creating'}
                  onClick={() => {
                    post(c.id, {
                      id: uid(),
                      kind: 'user',
                      text: driver
                        ? `Dispatch to ${firstName(driver.name)}`
                        : 'Save call',
                    });
                    create(c);
                  }}
                >
                  {driver
                    ? `Dispatch to ${firstName(driver.name)}`
                    : 'Save call'}
                </Button>
                <Button
                  variant="secondary"
                  size="xl"
                  pill
                  fullWidth
                  disabled={c.phase === 'creating'}
                  onClick={() => {
                    update(c.id, () => ({ driversOpen: true }));
                    scrollToCard();
                    toast.info('Tap anything above to change it');
                  }}
                >
                  Change something
                </Button>
                <Text variant="body-sm" tone="muted" align="center">
                  You can also type yes or no.
                </Text>
              </Stack>
            )}
          </Stack>
        </CardBody>
      </Card>
    );
  };

  const createdActions = (c: Chat) => (
    <Inline gap={2} wrap>
      <Button
        variant="soft"
        pill
        size="lg"
        leadingIcon={<ArrowSquareOut />}
        onClick={() =>
          navigate(c.created?.id ? `/calls/${c.created.id}` : '/calls')
        }
      >
        {c.created?.id ? 'Open call' : 'Open calls'}
      </Button>
      <Button variant="secondary" pill size="lg" onClick={() => startChat()}>
        New call
      </Button>
      <Button variant="secondary" pill size="lg" onClick={() => copySummary(c)}>
        Copy summary
      </Button>
    </Inline>
  );

  const renderMessage = (c: Chat, message: Message) => {
    switch (message.kind) {
      case 'user':
        return (
          <ChatBubble
            key={message.id}
            text={message.text}
            pastedLabel="Pasted call"
          />
        );
      case 'assistant':
        return (
          <Text
            key={message.id}
            variant={message.muted ? 'body-sm' : 'body-lg'}
            tone={message.muted ? 'muted' : 'default'}
          >
            {message.text}
          </Text>
        );
      case 'card':
        return (
          <Stack key={message.id} gap={2}>
            <CallDraftCard
              chat={c}
              cardId={cardId}
              accounts={accountOptions}
              accountId={accountFor(c)?.id ?? null}
              editable={!c.created && c.phase !== 'creating'}
              actions={actions}
              revealFrom={stepOf(message)}
            />
            {c.reading ? (
              <ChatReveal step={stepOf(message) + CARD_STEPS - 1}>
                <Text variant="body-sm" tone="muted">
                  {c.reading.by === 'ai'
                    ? 'Read by Relay AI'
                    : 'Read with basic rules'}
                </Text>
              </ChatReveal>
            ) : null}
          </Stack>
        );
      case 'drivers':
        return (
          <React.Fragment key={message.id}>{driverPicker(c)}</React.Fragment>
        );
      case 'confirm':
        return (
          <React.Fragment key={message.id}>{confirmCard(c)}</React.Fragment>
        );
      case 'created':
        return (
          <React.Fragment key={message.id}>{createdActions(c)}</React.Fragment>
        );
    }
  };

  const hero = (
    <Stack gap={6}>
      <Stack gap={2}>
        <Text as="h1" variant="h1">
          Paste a call, we'll set it up.
        </Text>
        <Text tone="muted">
          Paste the text as you got it, or type a few words. We'll fill in the
          call, and you check it before it goes out.
        </Text>
      </Stack>
    </Stack>
  );

  const list = (
    <Stack gap={0} divided>
      {[...chats].reverse().map((one) => {
        const { street, city } = splitPickup(one.call.pickup);
        const driver = driverOf(one);
        const status = one.created
          ? driver
            ? 'DISPATCHED'
            : 'UNASSIGNED'
          : 'DRAFT';
        const statusLabel = one.created
          ? driver
            ? 'Dispatched'
            : 'Unassigned'
          : 'Draft';
        const weight = one.call.towType
          ? (one.call.towType.toLowerCase() as 'light' | 'medium' | 'heavy')
          : undefined;
        return (
          <CallCard
            platform="phone"
            key={one.id}
            address={street || 'New call'}
            locality={city || one.call.account || one.timeLabel}
            status={status}
            statusLabel={statusLabel}
            priority={one.call.priority}
            number={one.call.dispatchNumber || undefined}
            weightClass={weight}
            aria-label={`Open the call at ${street || 'new call'}`}
            onClick={() => {
              setCurrentId(one.id);
              setView('chat');
            }}
          />
        );
      })}
    </Stack>
  );

  const placeholder =
    chat.phase === 'confirm'
      ? 'Type yes or no, or tap a button'
      : chat.phase === 'driver'
        ? 'Tap a driver, or type a name'
        : 'Paste a call or describe it';

  const title =
    view === 'list'
      ? 'Calls'
      : chat.call.pickup
        ? splitPickup(chat.call.pickup).street
        : 'Dispatch';

  return (
    <MobileScreen
      narrow
      header={
        <MobileHeader
          onBack={view === 'list' ? () => setView('chat') : back}
          backLabel={view === 'list' ? 'Back to the open call' : 'Back'}
          title={title}
          action={
            view === 'list' ? (
              <Button
                variant="soft"
                size="md"
                pill
                leadingIcon={<Plus />}
                onClick={() => startChat()}
              >
                Call
              </Button>
            ) : (
              <Button
                variant="secondary"
                size="md"
                pill
                leadingIcon={<ListBullets />}
                onClick={() => setView('list')}
              >
                Calls
              </Button>
            )
          }
        />
      }
      footer={
        view === 'chat' ? (
          <ChatComposer
            value={draft}
            onChange={setDraft}
            onSend={send}
            onAdd={() => {
              if (
                chat.phase === 'idle' ||
                chat.phase === 'reading' ||
                chat.phase === 'pickup'
              )
                toast.info(
                  'Paste a call first, then add photos or notes to it'
                );
              else setAdding(true);
            }}
            addLabel="Add to call"
            label="Message Relay"
            placeholder={placeholder}
            sendDisabled={chat.phase === 'creating' || chat.phase === 'reading'}
            inputRef={inputRef}
          />
        ) : undefined
      }
      footerFill
    >
      {view === 'list' ? (
        list
      ) : (
        <Stack gap={6}>
          {chat.messages.length === 0 ? hero : null}
          {chat.messages.map((message) => (
            <ChatReveal
              key={message.id}
              step={stepOf(message)}
              data-message={message.id}
            >
              {renderMessage(chat, message)}
            </ChatReveal>
          ))}
          {chat.phase === 'reading' ? (
            <ChatWorking steps={READING_STEPS} />
          ) : null}
          {chat.phase === 'creating' ? (
            <ChatWorking steps={CREATING_STEPS} />
          ) : null}
        </Stack>
      )}
      <BottomSheet open={adding} onOpenChange={setAdding}>
        <BottomSheetContent title="Add to call">
          <Stack gap={4}>
            <Button
              variant="secondary"
              size="xl"
              pill
              fullWidth
              leadingIcon={<Camera />}
              onClick={() => {
                setAdding(false);
                if (chat.created?.id) navigate(`/calls/${chat.created.id}`);
                else
                  toast.info(
                    'Photos go on the call once it is created. Open it to add them.'
                  );
              }}
            >
              Add photos
            </Button>
            <Button
              variant="secondary"
              size="xl"
              pill
              fullWidth
              leadingIcon={<Car />}
              disabled={Boolean(chat.created)}
              onClick={() => {
                setAdding(false);
                openEdit(chat, 'vin');
              }}
            >
              Add VIN
            </Button>
            <Button
              variant="secondary"
              size="xl"
              pill
              fullWidth
              leadingIcon={<NotePencil />}
              disabled={Boolean(chat.created)}
              onClick={() => {
                setAdding(false);
                update(chat.id, () => ({ editingNotes: true }));
                window.setTimeout(scrollToCard, 30);
              }}
            >
              Add note
            </Button>
          </Stack>
        </BottomSheetContent>
      </BottomSheet>
    </MobileScreen>
  );
}
